// Dependencies
import { ApolloClient } from "@apollo/client";
import { parseISO } from "date-fns";
import { ObjectId } from "mongodb";

import { EmployeeField } from "../../../schemas/employee";
// Schema
import {
	AggregationOperation,
	FieldFilter,
	GridSort,
	IColumnMetadata,
	ISummarizeByMetadata,
	ISummaryColumnMetadata,
	Report,
	SavedQuery,
	SelectedFilter,
	SummarizeBy,
} from "../../../schemas/report";

// Endpoints
import {
	DELETE_QUESTION_GROUPINGS,
	GET_CUSTOM_FIELDS,
	GET_GROUPS,
	GET_NON_UNIQUE_QUESTIONS,
	GET_NON_UNIQUE_SURVEY_QUESTIONS,
	GET_QUESTION_GROUPING_SESSION,
	GET_RAW_EMPLOYEE_REPORT_DATA_CSV,
	GET_RAW_GOAL_REPORT_DATA_CSV,
	GET_RAW_REVIEW_REPORT_DATA_CSV,
	GET_RAW_SURVEY_REPORT_DATA_CSV,
	GET_REPORT_CSV_BY_ID,
	GET_REVIEW_CYCLES,
	GET_REVIEW_FORMS,
	GET_SAVED_QUERY,
	GET_SURVEY_NAMES,
	GET_UNIQUE_QUESTIONS,
	GET_UNIQUE_SURVEY_QUESTIONS,
	SAVE_REPORT_QUERY,
	UPDATE_SAVED_REPORT_QUERY,
} from "../../../hooks/useQuery";

import {
	getRbacCompanySettingDependentPermission,
	getRbacPrimaryRolePermissions,
} from "../../../../../api/services/defs/rbac";

import { defaultReportPageSize } from "../../../config";
import { IQuestion, ISurveyQuestion } from "../Components/QuestionGroupingModal/schema";
import { IRawQuestion } from "../Components/QuestionGroupingModal/schema";
import { ChartType, ReportAnalyticsRbacPermissions } from "./enums";
// utils
import { formatQuickFilter } from "./filters";

interface QuestionGroupingSessionResponse {
	_id: string;
	reviewQueryID: string | null;
	employeeQueryID: string | null;
	selectedQuestions: IRawQuestion[];
}

interface UniqueQuestionsResponse {
	_id: string;
	question: string;
	questionHash: string;
	questionType: string;
	reviewFormName: string;
}

interface NonUniqueQuestionsResponse {
	_id: string;
	question: string;
	questionHash: string;
	questionType: string;
	reviewFormName: string;
}

interface ReviewCyclesResponse {
	_id: string;
	name: string;
}

interface ReviewFormsResponse {
	_id: string;
	name: string;
}

interface GroupsResponse {
	_id: string;
	name: string;
}

interface SurveyNamesResponse {
	_id: string;
	name: string;
}

export const fetchSavedQuery = async (client: ApolloClient<any>, reportID: string): Promise<Report | null> => {
	try {
		const response = await client.query<{ savedQuery: SavedQuery }>({
			query: GET_SAVED_QUERY,
			variables: {
				queryID: reportID,
			},
			fetchPolicy: "no-cache",
		});

		const report = {
			name: response.data.savedQuery.name || "",
			type: response.data.savedQuery.type || "",
			description: response.data.savedQuery.description,
			tags: response.data.savedQuery.tags,
			createdAt: parseISO(response.data.savedQuery.createdAt),
			updatedAt: parseISO(response.data.savedQuery.updatedAt),
			questionGroupingSessionID: response.data.savedQuery.question_grouping_session_id,
			createdBy: {
				_id: response.data.savedQuery.createdBy?._id,
				name: response.data.savedQuery.createdBy?.name,
			},
			selectedColumns: response.data.savedQuery.selectedColumns,
			sorts: response.data.savedQuery.sorts || [],
			filters: response.data.savedQuery.filters || [],
			quickFilter: formatQuickFilter(response.data.savedQuery.quickFilter || []),
			columnMetadata: response.data.savedQuery.columnMetadata,
			summarizeBy: response.data.savedQuery.summarizeBy || [],
			aggregationOperations: response.data.savedQuery.aggregationOperations || {},
			isSummarizeRowsView: response.data.savedQuery.isSummarizeRowsView || false,
			showAllAggregationColumns: response.data.savedQuery.showAllAggregationColumns,
			summarizeByMetadata: response.data.savedQuery.summarizeByMetadata || {},
			visualizeOptions: response.data.savedQuery.visualizeOptions || {},
			pageSize: response.data.savedQuery.pageSize || defaultReportPageSize,
			sharedWith: response.data.savedQuery.sharedWith || [],
			deletedAt: response?.data?.savedQuery?.deletedAt || undefined,
		};

		return report;
	} catch (error) {
		console.log(error);
	}

	return null;
};

export const fetchGroupingSession = async (client: ApolloClient<any>, groupingSessionID: string) => {
	try {
		const response = await client.query<{
			questionGroupingSession: QuestionGroupingSessionResponse;
		}>({
			query: GET_QUESTION_GROUPING_SESSION,
			variables: {
				groupingSessionID: groupingSessionID,
			},
			fetchPolicy: "no-cache",
		});

		const questionGroupingSession = {
			reviewQueryID: response.data.questionGroupingSession.reviewQueryID,
			employeeQueryID: response.data.questionGroupingSession.employeeQueryID,
			selectedQuestions: response.data.questionGroupingSession.selectedQuestions.map((question) => {
				return {
					_id: question._id,
					name: question.name,
					questions: question.questions,
					isGrouped: question.is_grouped,
					summaryFunction: question.summary_function,
				};
			}),
		};

		return questionGroupingSession;
	} catch (error) {
		console.log(error);
	}

	return null;
};

export const fetchUniqueReviewCycles = async (client: ApolloClient<any>) => {
	try {
		const response = await client.query<{
			uniqueReviewCycleNames: ReviewCyclesResponse[];
		}>({
			query: GET_REVIEW_CYCLES,
		});

		const reviewCycles = response.data.uniqueReviewCycleNames;
		return reviewCycles;
	} catch (error) {
		return [];
	}
};

export const fetchUniqueReviewForms = async (client: ApolloClient<any>) => {
	try {
		const response = await client.query<{
			uniqueReviewFormNames: ReviewFormsResponse[];
		}>({
			query: GET_REVIEW_FORMS,
		});

		const reviewCycles = response.data.uniqueReviewFormNames;
		return reviewCycles;
	} catch (error) {
		return [];
	}
};

export const fetchGroups = async (client: ApolloClient<any>) => {
	try {
		const response = await client.query<{ groups: GroupsResponse[] }>({
			query: GET_GROUPS,
		});

		const groups = response.data.groups;
		return groups;
	} catch (error) {
		return [];
	}
};

export const fetchUniqueQuestions = async (
	client: ApolloClient<any>,
	groupingSessionID: string,
	filters: FieldFilter[][]
) => {
	try {
		const response = await client.query<{
			uniqueReviewReportQuestions: UniqueQuestionsResponse[];
		}>({
			query: GET_UNIQUE_QUESTIONS,
			variables: {
				questionGroupingSessionID: groupingSessionID,
				filters: filters,
			},
		});

		const uniqueReviewReportQuestions = response.data.uniqueReviewReportQuestions as IQuestion[];
		return uniqueReviewReportQuestions;
	} catch (error) {
		console.log(error);
	}

	return null;
};

export const fetchNonUniqueQuestions = async (client: ApolloClient<any>, groupingSessionID: string) => {
	try {
		const response = await client.query<{
			nonUniqueReviewReportQuestions: NonUniqueQuestionsResponse[];
		}>({
			query: GET_NON_UNIQUE_QUESTIONS,
			variables: {
				questionGroupingSessionID: groupingSessionID,
			},
		});

		const nonUniqueReviewReportQuestions = response.data.nonUniqueReviewReportQuestions as IQuestion[];
		return nonUniqueReviewReportQuestions;
	} catch (error) {
		console.log(error);
	}

	return null;
};

export const fetchUniqueSurveyQuestions = async (
	client: ApolloClient<any>,
	groupingSessionID: string,
	filters: FieldFilter[][]
) => {
	try {
		const response = await client.query<{
			uniqueSurveyQuestions;
		}>({
			query: GET_UNIQUE_SURVEY_QUESTIONS,
			variables: {
				questionGroupingSessionID: groupingSessionID,
				filters: filters,
			},
		});

		const uniqueSurveyQuestions = response.data.uniqueSurveyQuestions as ISurveyQuestion[];

		return uniqueSurveyQuestions;
	} catch (error) {
		console.log(error);
	}

	return null;
};
export const fetchNonUniqueSurveyQuestions = async (
	client: ApolloClient<any>,
	groupingSessionID: string,
	filters: FieldFilter[][]
) => {
	try {
		const response = await client.query<{
			nonUniqueSurveyQuestions: NonUniqueQuestionsResponse[];
		}>({
			query: GET_NON_UNIQUE_SURVEY_QUESTIONS,
			variables: {
				questionGroupingSessionID: groupingSessionID,
				filters: filters,
			},
		});

		const nonUniqueSurveyQuestions = response.data.nonUniqueSurveyQuestions as unknown as ISurveyQuestion[];
		return nonUniqueSurveyQuestions;
	} catch (error) {
		console.log(error);
	}

	return null;
};

export const fetchEmployeeFields = async (client: ApolloClient<any>, report: any) => {
	const user_permissions = await getRbacPrimaryRolePermissions("report_analytics");

	try {
		const response = await client.query<{ fields: EmployeeField[] }>({
			query: GET_CUSTOM_FIELDS,
		});

		const employeeFields = response.data?.fields || [];

		if (
			!user_permissions?.includes(ReportAnalyticsRbacPermissions.CreateReport) &&
			!user_permissions?.includes(ReportAnalyticsRbacPermissions.CreateEmployeeReport)
		) {
			const managerReportingEnabled = await getRbacCompanySettingDependentPermission(
				"managers-can-view-report-analytics",
				"report_analytics",
				"report_analytics:viewReportsListSharedWithManagers",
				"ff-is-manager-and-contributor-reporting-access-enabled"
			);
			const contributorReportingEnabled = await getRbacCompanySettingDependentPermission(
				"contributors-can-view-report-analytics",
				"report_analytics",
				"report_analytics:viewReportsListSharedWithContributors",
				"ff-is-manager-and-contributor-reporting-access-enabled"
			);
			// Check if user has both manager and contributor permissions
			const hasManagerAndContributorPermissions =
				user_permissions?.includes(ReportAnalyticsRbacPermissions.ViewReportSharedWithManagers) &&
				user_permissions?.includes(ReportAnalyticsRbacPermissions.ViewReportSharedWithContributors);

			// Check if user has only contributor permissions
			const hasOnlyContributorPermissions = user_permissions?.includes(
				ReportAnalyticsRbacPermissions.ViewReportSharedWithContributors
			);

			const hasOnlyManagersPermissions = user_permissions?.includes(
				ReportAnalyticsRbacPermissions.ViewReportSharedWithManagers
			);

			if (
				(hasManagerAndContributorPermissions || hasOnlyManagersPermissions) &&
				report?.sharedWith?.includes("managers") &&
				managerReportingEnabled
			) {
				return employeeFields?.filter(
					(column) => column?.vis?.includes("manager") || column?.vis?.includes("all")
				);
			}

			if (
				hasOnlyContributorPermissions &&
				report?.sharedWith?.includes("contributors") &&
				contributorReportingEnabled
			) {
				return employeeFields?.filter((column) => column?.vis?.includes("all"));
			}

			return [];
		}

		return employeeFields;
	} catch (error) {
		console.log(error);
	}

	return [];
};

export const fetchReportCSV = async (
	client: ApolloClient<any>,
	questionGroupingSessionID: string,
	columns: { id: string; type: string }[],
	filters: FieldFilter[][],
	sorts: GridSort[],
	reportName: string,
	summarizeBy: SummarizeBy[],
	aggregationOperations: AggregationOperation[],
	isSummarizeRowsView: boolean,
	summaryColumnMetadata: { [key: string]: ISummaryColumnMetadata },
	summarizeByMetadata: { [key: string]: ISummarizeByMetadata }
) => {
	try {
		const response = await client.query<{ reviewReportsCSV: string }>({
			query: GET_RAW_REVIEW_REPORT_DATA_CSV,
			variables: {
				questionGroupingSessionID: questionGroupingSessionID,
				columns: columns,
				filters: filters,
				// CSV queries ignore pagination params
				pageNumber: "1",
				pageSize: "999",
				sorts: sorts,
				reportName: reportName,
				summarizeBy: summarizeBy,
				aggregationOperations: aggregationOperations,
				isSummarizeRowsView: isSummarizeRowsView,
				summaryColumnMetadata: summaryColumnMetadata,
				summarizeByMetadata: summarizeByMetadata,
			},
		});

		const jobID = response.data.reviewReportsCSV;

		return jobID;
	} catch (error) {
		console.log(error);
	}

	return null;
};

export const fetchReportCSVByID = async (client: ApolloClient<any>, reportID: string) => {
	try {
		const response = await client.query<{ reviewReportsCSVByID: string }>({
			query: GET_REPORT_CSV_BY_ID,
			variables: {
				reportID: reportID,
			},
			fetchPolicy: "network-only",
		});

		const jobID = response.data.reviewReportsCSVByID;

		return jobID;
	} catch (error) {
		console.log(error);
	}

	return null;
};

export const removeQuestionGrouping = async (
	client: ApolloClient<any>,
	questionGroupingSessionID: string,
	groupID: string
) => {
	try {
		const response = await client.mutate({
			mutation: DELETE_QUESTION_GROUPINGS,
			variables: {
				questionGroupingSessionID,
				groupIDs: [groupID],
			},
		});
		return response;
	} catch (error) {
		console.log(error);
	}
};

export const saveAsReportQuery = async (
	client: ApolloClient<any>,
	variables: {
		employeeQueryID: string | null;
		savedQuery: {
			type: "employeeReport" | "reviewReport" | "goalReport" | "surveyReport";
			name: string;
			description: string;
			tags: string[];
			conditions?: FieldFilter[][] | null;
		};
		selectedColumns: string[];
		columnMetadata: { [key: string]: IColumnMetadata };
		quickFilter: SelectedFilter[];
		sorts: GridSort[];
		summarizeBy: string[];
		aggregationOperations: { [key: string]: string };
		isSummarizeRowsView: boolean;
		showAllAggregationColumns: boolean;
		summaryColumnMetadata: { [key: string]: ISummaryColumnMetadata };
		questionGroupingSessionID?: string;
		summarizeByMetadata: { [key: string]: ISummarizeByMetadata };
		visualizeOptions: Partial<Record<ChartType | "selectedChart", any>>;
		pageSize?: number;
	}
) => {
	try {
		const response = await client.mutate({
			mutation: SAVE_REPORT_QUERY,
			variables,
		});
		return response;
	} catch (error) {
		console.log(error);
	}
};

export const updateReportQuery = async (
	client: ApolloClient<any>,
	variables: {
		queryID: string;
		savedQuery: {
			type: "employeeReport" | "reviewReport" | "goalReport" | "surveyReport";
			name: string;
			description: string;
			tags: string[];
			conditions?: FieldFilter[][] | null;
		};
		selectedColumns: string[];
		columnMetadata: { [key: string]: IColumnMetadata };
		quickFilter: SelectedFilter[];
		sorts: GridSort[];
		summarizeBy: string[];
		aggregationOperations: { [key: string]: string };
		isSummarizeRowsView: boolean;
		showAllAggregationColumns: boolean;
		summaryColumnMetadata: { [key: string]: ISummaryColumnMetadata };
		summarizeByMetadata: { [key: string]: ISummarizeByMetadata };
		visualizeOptions: Partial<Record<ChartType | "selectedChart", any>>;
		pageSize?: number;
		sharedWith?: string[];
	}
) => {
	try {
		const response = await client.mutate({
			mutation: UPDATE_SAVED_REPORT_QUERY,
			variables,
		});
		return response;
	} catch (error) {
		console.log(error);
	}
};

export const fetchEmployeeReportCSV = async (
	client: ApolloClient<any>,
	columns: { id: string; type: string }[],
	filters: FieldFilter[][],
	sorts: GridSort[],
	reportName: string,
	summarizeBy: SummarizeBy[],
	aggregationOperations: AggregationOperation[],
	isSummarizeRowsView: boolean,
	summaryColumnMetadata: { [key: string]: ISummaryColumnMetadata },
	summarizeByMetadata: { [key: string]: ISummarizeByMetadata }
) => {
	try {
		const response = await client.query<{ employeeReportsCSV: string }>({
			query: GET_RAW_EMPLOYEE_REPORT_DATA_CSV,
			variables: {
				columns: columns,
				filters: filters,
				// CSV queries ignore pagination params
				pageNumber: "1",
				pageSize: "999",
				sorts: sorts,
				reportName: reportName,
				summarizeBy: summarizeBy,
				aggregationOperations: aggregationOperations,
				isSummarizeRowsView: isSummarizeRowsView,
				summaryColumnMetadata: summaryColumnMetadata,
				summarizeByMetadata: summarizeByMetadata,
			},
		});

		const jobID = response.data.employeeReportsCSV;

		return jobID;
	} catch (error) {
		console.log(error);
	}

	return null;
};

export const fetchGoalReportCSV = async (
	client: ApolloClient<any>,
	columns: { id: string; type: string }[],
	filters: FieldFilter[][],
	sorts: GridSort[],
	reportName: string,
	summarizeBy: SummarizeBy[],
	aggregationOperations: AggregationOperation[],
	isSummarizeRowsView: boolean,
	summaryColumnMetadata: { [key: string]: ISummaryColumnMetadata },
	summarizeByMetadata: { [key: string]: ISummarizeByMetadata },
	showAllAggregationColumns: boolean = false
) => {
	try {
		const response = await client.query<{ goalReportsCSV: string }>({
			query: GET_RAW_GOAL_REPORT_DATA_CSV,
			variables: {
				columns: columns,
				filters: filters,
				// CSV queries ignore pagination params
				pageNumber: "1",
				pageSize: "999",
				sorts: sorts,
				reportName: reportName,
				summarizeBy: summarizeBy,
				aggregationOperations: aggregationOperations,
				isSummarizeRowsView: isSummarizeRowsView,
				summaryColumnMetadata: summaryColumnMetadata,
				summarizeByMetadata: summarizeByMetadata,
				showAllAggregationColumns: showAllAggregationColumns,
			},
		});

		const jobID = response.data.goalReportsCSV;

		return jobID;
	} catch (error) {
		console.log(error);
	}

	return null;
};

export const fetchUniqueSurveys = async (client: ApolloClient<any>) => {
	try {
		const response = await client.query<{
			uniqueSurveyNames: SurveyNamesResponse[];
		}>({
			query: GET_SURVEY_NAMES,
		});

		const surveys = response.data.uniqueSurveyNames;
		return surveys;
	} catch (error) {
		return [];
	}
};

export const fetchSurveyReportCSV = async (
	client: ApolloClient<any>,
	columns: { id: string; type: string }[],
	filters: FieldFilter[][],
	sorts: GridSort[],
	reportName: string,
	questionGroupingSessionID: ObjectId | string,
	summarizeBy: SummarizeBy[],
	aggregationOperations: AggregationOperation[],
	isSummarizeRowsView: boolean,
	summaryColumnMetadata: { [key: string]: ISummaryColumnMetadata },
	summarizeByMetadata: { [key: string]: ISummarizeByMetadata },
	showAllAggregationColumns: boolean
) => {
	try {
		const response = await client.query<{ surveyReportsCSV: string }>({
			query: GET_RAW_SURVEY_REPORT_DATA_CSV,
			variables: {
				columns: columns,
				filters: filters,
				// CSV queries ignore pagination params
				pageNumber: "1",
				pageSize: "999",
				sorts: sorts,
				reportName: reportName,
				summarizeBy: summarizeBy,
				aggregationOperations: aggregationOperations,
				isSummarizeRowsView: isSummarizeRowsView,
				summaryColumnMetadata: summaryColumnMetadata,
				summarizeByMetadata: summarizeByMetadata,
				questionGroupingSessionID: questionGroupingSessionID,
				showAllAggregationColumns: showAllAggregationColumns,
			},
		});

		const jobID = response.data.surveyReportsCSV;

		return jobID;
	} catch (error) {
		console.log(error);
	}

	return null;
};
